import React, { createContext, useContext, useState, useEffect } from 'react';
import AdminApiService from '../services/api.service';

// Create authentication context
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state
  useEffect(() => {
    console.log("Auth context initializing");
    
    const checkAuth = async () => {
      try {
        setLoading(true);
        console.log("Checking authentication state");
        
        // Check if we have a token in localStorage
        const token = localStorage.getItem('adminToken');
        const refreshTokenValue = localStorage.getItem('adminRefreshToken');
        
        console.log("Tokens in localStorage:", { 
          token: token ? "present" : "missing", 
          refreshToken: refreshTokenValue ? "present" : "missing" 
        });
        
        if (!token) {
          console.log("No token found, user is not authenticated");
          setLoading(false);
          return;
        }
        
        // Try multiple authentication methods in sequence with error handling
        // This makes our authentication more resilient to temporary failures
        
        // Keep track of all errors for debugging
        const errors = [];
        
        // Method 1: Try getting user data first (most direct)
        try {
          console.log("Attempting to get user data with current token");
          const userData = await AdminApiService.auth.getUserData();
          console.log('Successfully retrieved user data:', userData);
          setUser(userData.user || userData);
          setLoading(false);
          return; // Exit early on success
        } catch (userDataErr) {
          console.log('Failed to get user data with current token', userDataErr);
          errors.push({ method: 'getUserData', error: userDataErr });
          // Continue to next method
        }
        
        // Method 2: Try token verification
        try {
          console.log("Attempting token verification");
          const userData = await AdminApiService.auth.verifyToken();
          console.log('Token verification successful:', userData);
          setUser(userData.user || userData);
          setLoading(false);
          return; // Exit early on success
        } catch (verifyErr) {
          console.log('Token verification failed', verifyErr);
          errors.push({ method: 'verifyToken', error: verifyErr });
          // Continue to next method
        }
        
        // Method 3: Try token refresh
        if (refreshTokenValue) {
          try {
            console.log("Attempting token refresh");
            const refreshed = await refreshToken();
            
            if (refreshed) {
              console.log('Token refresh successful, getting user data');
              try {
                // Try to get user data with the new token
                const userData = await AdminApiService.auth.getUserData();
                console.log('User data retrieved after token refresh:', userData);
                setUser(userData.user || userData);
                setLoading(false);
                return; // Exit early on success
              } catch (postRefreshErr) {
                console.error('Failed to get user data after token refresh:', postRefreshErr);
                errors.push({ method: 'getUserDataAfterRefresh', error: postRefreshErr });
              }
            } else {
              console.log('Token refresh returned false, session likely expired');
              errors.push({ method: 'refreshToken', error: 'Refresh returned false' });
            }
          } catch (refreshErr) {
            console.error('Error during token refresh:', refreshErr);
            errors.push({ method: 'refreshToken', error: refreshErr });
          }
        } else {
          console.log('No refresh token available, cannot refresh');
          errors.push({ method: 'refreshToken', error: 'No refresh token available' });
        }
        
        // Method 4: Last chance - try a different endpoint that might not need strict auth
        // This is a fallback mechanism that might work when other methods fail
        try {
          console.log("Last resort - trying businesses endpoint with current token");
          // Attempt to list businesses which might work with the existing token
          // even if other endpoints are more strict
          await AdminApiService.businesses.getAll();
          
          // If we get here, the request succeeded, which means our token is probably valid
          // Try getting user data again
          try {
            const userData = await AdminApiService.auth.getUserData();
            console.log('Successfully retrieved user data on second attempt:', userData);
            setUser(userData.user || userData);
            setLoading(false);
            return; // Exit if successful
          } catch (secondUserDataErr) {
            console.log('Failed to get user data on second attempt', secondUserDataErr);
            errors.push({ method: 'getUserDataSecondAttempt', error: secondUserDataErr });
          }
        } catch (fallbackErr) {
          console.log('Fallback authentication attempt failed', fallbackErr);
          errors.push({ method: 'fallbackAttempt', error: fallbackErr });
        }
        
        // If we reach here, all authentication methods failed
        console.error('All authentication methods failed:', errors);
        
        // Set a more informative error message
        setError('Your session could not be verified. Please log in again.');
        setUser(null);
      } catch (error) {
        console.error('Unhandled auth error:', error);
        setError('Authentication check failed. Please refresh the page or log in again.');
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      
      // Show a message in the console to indicate authentication is in progress
      console.log('Authentication in progress - attempting login...');
      
      const userData = await AdminApiService.auth.login(email, password);
      console.log('Login successful, user data:', userData);
      
      if (userData) {
        setUser(userData);
        // Set a timestamp of the last successful authentication
        localStorage.setItem('lastAuthTime', Date.now().toString());
      } else {
        console.error('Login returned undefined user data');
        setError('Invalid response from server');
        throw new Error('Invalid response from server');
      }
      
      return userData;
    } catch (error) {
      console.error('Login failed:', error);
      setError(error.message || 'Login failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Refresh token function
  const refreshToken = async () => {
    try {
      console.log("AuthContext refreshToken: Starting token refresh");
      
      // ApiService handles token refresh internally
      const refreshResult = await AdminApiService.auth.refreshToken();
      console.log("Token refresh result:", refreshResult);
      
      // Verify we have a new token in localStorage after refresh
      const newToken = localStorage.getItem('adminToken');
      if (!newToken) {
        console.error("Token refresh appeared to succeed but no new token found in localStorage");
        return false;
      }
      
      console.log("New token saved to localStorage");
      return true;
    } catch (error) {
      console.error('Refresh token error:', error);
      return false;
    }
  };

  // Logout function
  const logout = () => {
    // Clear authentication tokens, but don't clear business selection
    // This helps with page refresh scenarios
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    
    // Update user state
    setUser(null);
  };

  // Helper function to check if user has a specific role
  const hasRole = (role) => {
    if (!user) return false;
    return user.roles && user.roles.includes(role);
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    refreshToken,
    isAuthenticated: !!user,
    isPlatformAdmin: user ? user.is_platform_admin : false,
    isBusinessOwner: hasRole('business_owner'),
    isBusinessStaff: hasRole('business_staff'),
    hasRole,
    roles: user ? user.roles : []
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use the auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}